import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { motion } from "framer-motion";
import { Building2, AlertTriangle, MessageSquareText, FileText } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="border-border">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-heading font-bold">{value}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const { stats, cards } = data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Live status across every active project.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Active Projects" value={stats.active_projects} accent="bg-primary/10 text-primary" />
        <Stat icon={AlertTriangle} label="Slipping" value={stats.slipping} accent="bg-red-50 text-red-600" />
        <Stat icon={MessageSquareText} label="Messages" value={stats.total_messages} accent="bg-secondary/10 text-secondary" />
        <Stat icon={FileText} label="Summaries" value={stats.total_summaries} accent="bg-chart-4/20 text-amber-700" />
      </div>

      <div>
        <h2 className="font-heading text-xl font-semibold mb-3">Active Projects</h2>
        {cards.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center text-muted-foreground">
              No active projects yet. Create a project and set its status to ACTIVE.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c, i) => (
              <motion.div
                key={c.project.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  className="border-border cursor-pointer hover:shadow-md hover:-translate-y-1 transition-transform duration-200"
                  onClick={() => navigate(`/projects/${c.project.id}`)}
                  data-testid={`dashboard-project-${c.project.id}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-heading font-semibold">{c.project.name}</div>
                        <div className="text-xs font-mono text-muted-foreground">{c.project.code}</div>
                      </div>
                      <StatusBadge status={c.health} />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{c.completed_stages}/{c.total_stages} stages</span>
                        <span>{c.progress_pct}%</span>
                      </div>
                      <Progress value={c.progress_pct} className="h-2" />
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Current: </span>
                      {c.current_stage ? (
                        <span className="font-medium">{c.current_stage.stage_name}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
